import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Switch,
  FormControlLabel,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import Swal from "sweetalert2";

interface UserSettingsData {
  useStock: boolean;
  useVatNumber: boolean;
  defaultVatRate: number;
  companyName: string;
}

const UserSettings: React.FC = () => {
  const [settings, setSettings] = useState<UserSettingsData>({
    useStock: false,
    useVatNumber: false,
    defaultVatRate: 18,
    companyName: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setSettings({
            useStock: data.useStock ?? false,
            useVatNumber: data.useVatNumber ?? false,
            defaultVatRate: data.defaultVatRate ?? 18,
            companyName: data.companyName ?? "",
          });
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
        Swal.fire("Error", "Failed to load settings", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleToggleStock = () => {
    setSettings({ ...settings, useStock: !settings.useStock });
    setChanged(true);
  };

  const handleToggleVatNumber = () => {
    setSettings({ ...settings, useVatNumber: !settings.useVatNumber });
    setChanged(true);
  };

  const handleVatRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setSettings({ ...settings, defaultVatRate: value });
    setChanged(true);
  };

  const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSettings({ ...settings, companyName: e.target.value });
    setChanged(true);
  };

  const handleSaveSettings = async () => {
    const user = auth.currentUser;
    if (!user) return;

    setSaving(true);
    try {
      await updateDoc(doc(db, "users", user.uid), {
        useStock: settings.useStock,
        useVatNumber: settings.useVatNumber,
        defaultVatRate: settings.defaultVatRate,
        companyName: settings.companyName,
      });

      setChanged(false);
      Swal.fire("Success", "Settings saved successfully", "success");
    } catch (error) {
      console.error("Error saving settings:", error);
      Swal.fire("Error", "Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="bg-white shadow-md rounded p-4">
      <Typography variant="h4" sx={{ mb: 3 }}>
        Settings
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardHeader title="Invoice Features" />
        <CardContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.useStock}
                  onChange={handleToggleStock}
                />
              }
              label="Enable Stock Management"
              sx={{ mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              Enable stock tracking for your invoices. You'll be able to manage
              inventory and track stock usage in the invoice generator.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={settings.useVatNumber}
                  onChange={handleToggleVatNumber}
                />
              }
              label="Show VAT Number Fields"
              sx={{ mt: 3, mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              Show VAT number fields for clients and company in invoices.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardHeader title="Default Invoice Settings" />
        <CardContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <label
                htmlFor="companyName"
                style={{ display: "block", marginBottom: "8px" }}
              >
                Company Name
              </label>
              <input
                id="companyName"
                type="text"
                value={settings.companyName}
                onChange={handleCompanyNameChange}
                placeholder="Enter your company name"
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
            </Box>

            <Box>
              <label
                htmlFor="vatRate"
                style={{ display: "block", marginBottom: "8px" }}
              >
                Default VAT Rate (%)
              </label>
              <input
                id="vatRate"
                type="number"
                value={settings.defaultVatRate}
                onChange={handleVatRateChange}
                placeholder="18"
                step="0.01"
                min="0"
                max="100"
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {changed && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have unsaved changes
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveSettings}
          disabled={!changed || saving}
        >
          {saving ? <CircularProgress size={24} /> : "Save Settings"}
        </Button>
      </Box>
    </Box>
  );
};

export default UserSettings;
